import React, { useEffect, Suspense } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { SignIn, SignUp, useAuth, useUser } from "@clerk/clerk-react";
import useAnalytics from "./hooks/useAnalytics";
import './components/Visualizations/ChartSetup';
// Guards/wrappers are small and used across many routes — keep them eager.
import RequireSubscription from "./components/RequireSubscription";
import { useFirebaseAuthReady } from "./hooks/useFirebaseAuthReady";
import RequireFeature from "./components/RequireFeature";
import TrialExpiredRedirect from "./components/TrialExpiredRedirect";
import { lazyWithRetry } from "./utils/lazyWithRetry";

// Route targets are code-split via lazyWithRetry (React.lazy + auto-reload on a
// post-deploy stale-chunk miss) so the initial bundle only loads what a given
// route needs (was a single ~4.3MB main chunk). Behaviour is unchanged — only
// WHEN each page's code downloads. All targets are default exports.
const FormBuilder = lazyWithRetry(() => import("./pages/FormBuilder"));
const FillForm = lazyWithRetry(() => import("./pages/FillForm"));
const Home = lazyWithRetry(() => import("./pages/Home"));
const EditForm = lazyWithRetry(() => import("./pages/EditForm"));
const Confirmation = lazyWithRetry(() => import("./pages/Confirmation"));
const UserManagement = lazyWithRetry(() => import("./components/UserManagement"));
const Landing = lazyWithRetry(() => import("./pages/Landing"));
const UserProfile = lazyWithRetry(() => import("./components/UserProfile"));
const ResponseDashboard = lazyWithRetry(() => import("./pages/ResponseDashboard"));
const SubmissionsHistory = lazyWithRetry(() => import("./pages/SubmissionsHistory"));
const CompanyLandingPage = lazyWithRetry(() => import("./pages/CompanyLandingPage"));
const LandingPageBuilder = lazyWithRetry(() => import("./pages/LandingPageBuilder"));
const FormSlugHandler = lazyWithRetry(() => import("./pages/FormSlugHandler"));
const ApprovalManager = lazyWithRetry(() => import("./pages/ApprovalManager"));
const ApprovalCalendarView = lazyWithRetry(() => import("./pages/ApprovalCalendarView"));
const PublicApproval = lazyWithRetry(() => import("./pages/PublicApproval"));
const DataRequest = lazyWithRetry(() => import("./pages/DataRequest"));
const RequestDataAccess = lazyWithRetry(() => import("./pages/RequestDataAccess"));
const UnsubscribeReminder = lazyWithRetry(() => import("./pages/UnsubscribeReminder"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/payment-success"));
const PaymentUpgrade = lazyWithRetry(() => import("./pages/payment-upgrade"));
const SubscriptionManagement = lazyWithRetry(() => import("./components/SubscriptionManagement"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Features = lazyWithRetry(() => import("./pages/Features"));
const BookDemo = lazyWithRetry(() => import("./pages/BookDemo"));
const AdminPanelUnified = lazyWithRetry(() => import("./pages/AdminPanelUnified"));
const DeleteAccount = lazyWithRetry(() => import("./pages/DeleteAccount"));
const CompanyManagement = lazyWithRetry(() => import("./pages/CompanyManagement"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));

// Wrapper components to fix Clerk TypeScript issues
const SignedOutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth();
  
  if (!isLoaded) return null;
  if (isSignedIn) return null;
  
  return <>{children}</>;
};

const SignedInWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const firebaseReady = useFirebaseAuthReady();

  if (!isLoaded) return null;
  if (!isSignedIn) return null;
  // Wait for the Clerk->Firebase bridge sign-in before rendering authed pages,
  // so their Firestore reads don't fire before request.auth exists and get
  // denied under the enforced rules (first-load race). Falls open after a
  // timeout (see useFirebaseAuthReady) so a failed sign-in can't hang the UI.
  if (!firebaseReady) return null;

  return <>{children}</>;
};

// Create a wrapper component to handle authentication state
const AuthenticationWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track website analytics
  useAnalytics(user?.id);
  
  useEffect(() => {
    // Only run this check if Clerk has finished loading
    if (isLoaded) {
      // Public routes that don't require authentication
      const publicRoutes = [
        '/fill/', 
        '/confirmation',
        '/sign-in',
        '/sign-up',
        '/landing',
        '/payment-success',
        '/landing-page-builder',
        '/contact',
        '/features',
        '/book-demo',
        '/delete-account',
        '/public-approval/', // Add the new public approval route
        '/privacy',
      ];
      
      // Check if current path is a public route or matches company/form slug pattern
      const isPublicRoute = publicRoutes.some(route => 
        location.pathname.startsWith(route) || location.pathname === route
      );

      // Special case for company slug routes (/:companySlug or /:companySlug/:formSlug)
      const isCompanySlugRoute = /^\/[^/]+\/?([^/]+)?$/.test(location.pathname) && 
                                !location.pathname.startsWith('/home'); // Make sure /home isn't treated as a slug
      
      // Explicit check for the /home route to ensure it redirects to /landing when not signed in
      const isHomeRoute = location.pathname === '/home';

      // If user is not signed in and trying to access a protected route
      if (!isSignedIn && !isPublicRoute && !isCompanySlugRoute) {
        console.log('User not authenticated, redirecting to landing page');
        navigate('/landing', { replace: true });
      }
      
      // Special handling for /home when not signed in - always redirect to /landing
      if (!isSignedIn && isHomeRoute) {
        console.log('Unauthenticated user on /home, redirecting to landing page');
        navigate('/landing', { replace: true });
      }
    }
  }, [isSignedIn, isLoaded, location.pathname, navigate]);

  // Show nothing while Clerk is loading to prevent flashes
  if (!isLoaded) {
    return null;
  }

  return <>{children}</>;
};

// Fallback shown while a lazily-loaded route chunk downloads.
const RouteFallback: React.FC = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <div
      style={{
        width: 40, height: 40, border: "4px solid #e0e0e0", borderTopColor: "#1976d2",
        borderRadius: "50%", animation: "routeSpin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes routeSpin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const App: React.FC = () => {
  const location = useLocation();
  return (
    <AuthenticationWrapper>
      <TrialExpiredRedirect />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Landing Page (Only for Non-Logged-In Users) */}
        <Route path="/landing" element={
          <SignedOutWrapper>
            <Landing />
          </SignedOutWrapper>
        } />

        {/* Authentication Routes */}
        <Route path="/sign-in/*" element={
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '24px 0' }}>
            <SignIn routing="path" path="/sign-in" />
            {/* The prebuilt <SignIn> doesn't surface a reset link on this Clerk
                instance, so we link to our custom flow (src/pages/ForgotPassword.tsx). */}
            <RouterLink to="/forgot-password" style={{ color: '#4B5FAA', textDecoration: 'underline', fontSize: '14px' }}>
              Forgot your password?
            </RouterLink>
          </div>
        } />
        <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        
        {/* Payment Routes */}
        <Route path="/payment-success" element={<SignedInWrapper><PaymentSuccess /></SignedInWrapper>} />
        <Route path="/payment-upgrade" element={<SignedInWrapper><PaymentUpgrade /></SignedInWrapper>} />
        {/* Subscription Management - Regular users see their own subscription, admins can see admin panel */}
        <Route path="/subscription-management" element={<SignedInWrapper><SubscriptionManagement /></SignedInWrapper>} />

        {/* Protected Routes with Premium Requirement */}
        <Route path="/new-form" element={
          <SignedInWrapper>
            <RequireSubscription allowOneFreeForm={true}>
              <FormBuilder />
            </RequireSubscription>
          </SignedInWrapper>
        } />
        <Route path="/edit/:formId" element={
          <SignedInWrapper>
            <RequireSubscription allowOneFreeForm={true}>
              <EditForm />
            </RequireSubscription>
          </SignedInWrapper>
        } />
        <Route path="/responses/:formId" element={
          <SignedInWrapper>
            <RequireSubscription>
              <RequireFeature feature="analytics" redirect>
                <ResponseDashboard />
              </RequireFeature>
            </RequireSubscription>
          </SignedInWrapper>
        } />
        <Route path="/responses" element={
          <SignedInWrapper>
            <RequireSubscription>
              <RequireFeature feature="analytics" redirect>
                <ResponseDashboard />
              </RequireFeature>
            </RequireSubscription>
          </SignedInWrapper>
        } />
        <Route path="/forms/:formId/submissions" element={
          <SignedInWrapper>
            <RequireSubscription>
              <RequireFeature feature="analytics" redirect>
                <SubmissionsHistory />
              </RequireFeature>
            </RequireSubscription>
          </SignedInWrapper>
        } />
        
        {/* Protected Routes - Accessible with Sign-in but no Premium Required */}
        <Route path="/home" element={<SignedInWrapper><Home /></SignedInWrapper>} />
        {/* Redirect old admin pages to unified admin panel (only for admins) */}
        <Route path="/manage-users" element={<SignedInWrapper><Navigate to="/admin-panel?tab=users" replace /></SignedInWrapper>} />
        {/* Subscription Management - Regular users see their own subscription, admins redirected to admin panel */}
        <Route path="/subscription-management" element={<SignedInWrapper><SubscriptionManagement /></SignedInWrapper>} />
        <Route path="/admin-panel" element={<SignedInWrapper><AdminPanelUnified /></SignedInWrapper>} />
        <Route path="/company-management" element={<SignedInWrapper><RequireFeature feature="companyAccount"><CompanyManagement /></RequireFeature></SignedInWrapper>} />
        <Route path="/profile" element={<SignedInWrapper><UserProfile /></SignedInWrapper>} />
        <Route path="/landing-page-builder" element={
          <SignedInWrapper>
            <RequireFeature feature="landingPageBuilder" redirect>
              <LandingPageBuilder />
            </RequireFeature>
          </SignedInWrapper>
        } />
        <Route path="/approval-manager" element={
          <SignedInWrapper>
            <RequireFeature feature="approvalSystem" redirect>
              <ApprovalManager />
            </RequireFeature>
          </SignedInWrapper>
        } />
        
        {/* Contact Page - Available to all users */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        
        {/* Delete Account Page - Available to all users */}
        <Route path="/delete-account" element={<DeleteAccount />} />
        
        {/* Features and Demo Pages - Available to all users */}
        <Route path="/features" element={<Features />} />
        <Route path="/book-demo" element={<BookDemo />} />
        <Route path="/approval-manager/:formId" element={
          <SignedInWrapper>
            <RequireFeature feature="approvalSystem" redirect>
              <ApprovalManager />
            </RequireFeature>
          </SignedInWrapper>
        } />
        <Route path="/approval-manager/:formId/:submissionId" element={
          <SignedInWrapper>
            <RequireFeature feature="approvalSystem" redirect>
              <ApprovalManager />
            </RequireFeature>
          </SignedInWrapper>
        } />
        <Route path="/approval-manager/:formId/:submissionId/:status" element={
          <SignedInWrapper>
            <RequireFeature feature="approvalSystem" redirect>
              <ApprovalManager />
            </RequireFeature>
          </SignedInWrapper>
        } />
        <Route path="/approval-calendar/:formId" element={
          <SignedInWrapper>
            <RequireFeature feature="approvalSystem" redirect>
              <ApprovalCalendarView key={location.pathname} />
            </RequireFeature>
          </SignedInWrapper>
        } />
        <Route path="/approval-calendar/:formId/:submissionId" element={
          <SignedInWrapper>
            <RequireFeature feature="approvalSystem" redirect>
              <ApprovalCalendarView key={location.pathname} />
            </RequireFeature>
          </SignedInWrapper>
        } />

        {/* Public Routes (No sign-in required) */}
        <Route path="/fill/:formId" element={<FillForm />} />
        <Route path="/confirmation" element={<Confirmation />} />
        <Route path="/public-approval/:token" element={<PublicApproval />} />
        <Route path="/data-request/:token" element={<DataRequest />} />
        <Route path="/request-data-access" element={<RequestDataAccess />} />
        <Route path="/unsubscribe/:token" element={<UnsubscribeReminder />} />
        <Route path="/:companySlug/:formSlug" element={<FormSlugHandler />} />
        <Route path="/:companySlug" element={<CompanyLandingPage />} />
        
        {/* Default Route: Redirect to Home if Signed In, Otherwise Landing */}
        <Route path="*" element={
          <>
            <SignedInWrapper>
              <Navigate to="/home" />
            </SignedInWrapper>
            <SignedOutWrapper>
              <Navigate to="/landing" />
            </SignedOutWrapper>
          </>
        } />
      </Routes>
      </Suspense>
    </AuthenticationWrapper>
  );
};

export default App;